<template>
    <page :busy="busy" :tabs="tabs">
        <grid
            ref="grid"
            :rowData="rowData"
            gridTheme="ag-theme-alpine-report"
            :isSearch="false"
            :columnDefs="columnDefs"
            :isAutoWidth="true"
        >
            <date-range-picker
                :range-options="predefinedRanges"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="handleDaterangeChange"
            ></date-range-picker>
            <responsive-dropdown
                class="filter-button"
                :icon="['fas', 'filter']"
                :items="filterOptions"
                variant="primary"
                >{{ filterLabel }}
            </responsive-dropdown>
            <responsive-button
                :href="exportExcelHref"
                @click="generatingExcelReportSuccessToast"
                :icon="['fas', 'file-excel']"
                variant="primary"
            ></responsive-button>
            <export-to-pdf-button
                :href="exportPdfHref"
                :pdfCacheKey="pdfCacheKey"
                :pageOrientation="pageOrientation"
            />
            <responsive-dropdown
                :icon="['fas', 'file']"
                :items="orientationFileOption"
                variant="primary"
                >{{ orientationFileLabel }}
            </responsive-dropdown>
            <report-settings-modal
                :modalId="reportSettingsModalId"
                :initialValues="reportSettings"
            />
        </grid>
    </page>
</template>

<script>
import api from '../../api';
import DateRangePicker, {
    LAST_MONTH,
    LAST_WEEK,
    nameToRange,
    NEXT_MONTH,
    NEXT_WEEK,
    THIS_MONTH,
    THIS_WEEK,
    THIS_YEAR,
    TODAY,
    TOMORROW,
    YESTERDAY,
    ALL_DATES,
} from '../generic/date-range-picker/date-range-picker.vue';
import format from 'date-fns/esm/format/index';
import { mapGetters } from 'vuex';
import ResponsiveDropdown from '../generic/responsive-dropdown/responsive-dropdown.vue';
import Grid from '../generic/grid/grid.vue';
import page from '../generic/page/page.vue';
import { REPORT_GUEST_GENERATED_QUOTES_ENDPOINT } from '../../endpoints';
import ResponsiveButton from '../generic/responsive-button/responsive-button.vue';
import { escapeHTML } from '../../utils/strings';
import ReportSettingsModal, {
    DATE_PERIODS_INPUT,
    SELECT_INPUT,
} from './report-settings/report-settings-modal.vue';
import { defineAsyncComponent } from 'vue';

const exportToPdfButton = defineAsyncComponent(() =>
    import(
        /* webpackChunkName: "lx-export-to-pdf-button" */
        '../generic/export-to-pdf-button/export-to-pdf-button.vue'
    ))

export default {
    components: {
        page,
        Grid,
        exportToPdfButton,
        ResponsiveDropdown,
        DateRangePicker,
        ResponsiveButton,
        ReportSettingsModal,
    },
    data() {
        return {
            busy: null,
            dateRange: nameToRange(THIS_YEAR),
            dateRequestFormat: 'yyyy-MM-dd',
            filterBy: 'ENTERED_DATE',
            filterLabel: 'Filter By Entered Date',
            tabs: [
                {
                    title: 'Guest Generated Quotes',
                    active: true,
                },
            ],
            landscapeOrientationFileLabel: 'Landscape',
            orientationFileLabel: 'Landscape',
            portraitOrientationFileLabel: 'Portrait',
            pageOrientation: 'landscape',
            predefinedRanges: [
                YESTERDAY,
                TODAY,
                TOMORROW,
                LAST_WEEK,
                THIS_WEEK,
                NEXT_WEEK,
                LAST_MONTH,
                THIS_MONTH,
                NEXT_MONTH,
                THIS_YEAR,
                ALL_DATES,
            ],
            rowData: [],
            pdfCacheKey: null,
            reportSettingsModalId: 'report-settings-modal',
        };
    },
    computed: {
        ...mapGetters('header', ['userUnicodeDateFormat']),
        columnDefs() {
            return [
                {
                    headerName: 'Entered Date',
                    field: 'time_stamp',
                    type: 'dateColumn',
                    minWidth: 130,
                },
                {
                    headerName: 'To Name',
                    field: 'to_name',
                    minWidth: 250,
                    autoWidthExclude: true,
                },
                {
                    headerName: 'To Email',
                    field: 'to_email',
                    minWidth: 250,
                    autoWidthExclude: true,
                },
                {
                    headerName: 'From Name',
                    field: 'from_name',
                    minWidth: 250,
                    autoWidthExclude: true,
                },
                {
                    headerName: 'Property',
                    field: 'Property',
                    cellRenderer: ({ data }) =>
                        data.property
                            .map((item) => escapeHTML(item))
                            .join('<br/>'),
                    valueGetter: ({ data }) => data.property.join(', '),
                },
                {
                    headerName: 'Rental From',
                    field: 'from_date',
                    type: 'dateColumn',
                    minWidth: 130,
                },
                {
                    headerName: 'Rental To',
                    field: 'to_date',
                    type: 'dateColumn',
                    minWidth: 130,
                },

                {
                    headerName: 'View Quote',
                    field: 'quote_url',
                    cellRenderer: ({ data }) =>
                        `<a target="_blank" href="${data.quote_url}">View Quote</a>`,
                    minWidth: 110,
                    maxWidth: 110,
                },
                {
                    headerName: 'IP Address',
                    field: 'ip_address',
                },
            ];
        },
        endDateRequest() {
            return format(this.dateRange.to, this.dateRequestFormat);
        },
        exportPdfHref() {
            return `${REPORT_GUEST_GENERATED_QUOTES_ENDPOINT()}pdf/?from_date=${
                this.startDateRequest
            }&to_date=${this.endDateRequest}&filter_by=${this.filterBy}/`;
        },
        exportExcelHref() {
            return `${REPORT_GUEST_GENERATED_QUOTES_ENDPOINT()}excel?from_date=${
                this.startDateRequest
            }&to_date=${this.endDateRequest}&filter_by=${this.filterBy}`;
        },
        filterOptions() {
            return [
                {
                    label: 'Filter By Arrival Date',
                    click: () => {
                        this.filterBy = 'ARRIVAL_DATE';
                        this.filterLabel = 'Filter By Arrival Date';
                        this.fetchData();
                    },
                },
                {
                    label: 'Filter By Entered Date',
                    click: () => {
                        this.filterBy = 'ENTERED_DATE';
                        this.filterLabel = 'Filter By Entered Date';
                        this.fetchData();
                    },
                },
            ];
        },
        orientationFileOption() {
            return [
                {
                    label: this.landscapeOrientationFileLabel,
                    click: () => {
                        this.pageOrientation = 'landscape';
                        this.orientationFileLabel =
                            this.landscapeOrientationFileLabel;
                    },
                },
                {
                    label: this.portraitOrientationFileLabel,
                    click: () => {
                        this.pageOrientation = 'portrait';
                        this.orientationFileLabel =
                            this.portraitOrientationFileLabel;
                    },
                },
            ];
        },
        startDateRequest() {
            return format(this.dateRange.from, this.dateRequestFormat);
        },
        reportSettings() {
            return {
                name: 'Guest Generated Quotes',
                report_sysname: 'GUEST_GENERATED_QUOTES',
                settings: [
                    {
                        type: DATE_PERIODS_INPUT,
                        value: 'this_month',
                        predefinedRanges: this.predefinedRanges,
                        label: 'Period',
                        name: 'from_date',
                    },
                    {
                        type: SELECT_INPUT,
                        value: 'ARRIVAL_DATE',
                        name: 'filter_by',
                        options: [
                            {
                                text: 'Filter By Arrival Date',
                                value: 'ARRIVAL_DATE',
                            },
                            {
                                text: 'Filter By Entered Date',
                                value: 'ENTERED_DATE',
                            },
                        ],
                        label: 'Filter By',
                    },
                ],
            };
        },
    },
    mounted() {
        this.fetchData();
    },
    methods: {
        async fetchData() {
            try {
                this.busy = true;
                const { data } = await api.get(
                    REPORT_GUEST_GENERATED_QUOTES_ENDPOINT(),
                    {
                        params: {
                            from_date: this.startDateRequest,
                            to_date: this.endDateRequest,
                            filter_by: this.filterBy,
                        },
                    }
                );
                this.rowData = data.items;
                this.pdfCacheKey = data.pdf_cache_key;
            } catch (e) {
                this.errorToast({ text: 'Could not fetch quotes.' });
            } finally {
                this.busy = false;
            }
        },
        handleDaterangeChange(value) {
            this.dateRange = { ...value };
            this.fetchData();
        },
    },
};
</script>

<style>
</style>